<template>
  <div class="three-template">
    <!-- Название проекта -->
    <div class="three-template__header">
      <BaseTypography with-pre-line type="h1" weight="600">
        {{ data.title }}
      </BaseTypography>
      <BaseTypography with-pre-line type="text" weight="400">
        {{ data.description }}
      </BaseTypography>
      <TemplateLandingButtonView
        v-if="buttons.header"
        :button="buttons.header"
      />
    </div>

    <div class="three-template__main-section">
      <div v-if="data.images.leftMain" class="three-template__main-image">
        <TemplateMediaView
          :src="data.images.leftMain"
          :aspect-width="906"
          :aspect-height="390"
        />
      </div>
      <div v-if="data.images.rightMain" class="three-template__side-image">
        <TemplateMediaView
          :src="data.images.rightMain"
          :aspect-width="390"
          :aspect-height="390"
        />
      </div>
    </div>

    <!-- Три заголовка H2 в ряд -->
    <div class="three-template__triple-headers">
      <div class="three-template__header-item">
        <BaseTypography with-pre-line type="h3" weight="600">
          {{ data.leftSectionTitle }}
        </BaseTypography>
        <BaseTypography with-pre-line type="text" weight="400">
          {{ data.leftSectionDescription }}
        </BaseTypography>
        <TemplateLandingButtonView
          v-if="buttons.leftHeader"
          full-width
          :button="buttons.leftHeader"
        />
      </div>
      <div class="three-template__header-item">
        <BaseTypography with-pre-line type="h3" weight="600">
          {{ data.centerSectionTitle }}
        </BaseTypography>
        <BaseTypography with-pre-line type="text" weight="400">
          {{ data.centerSectionDescription }}
        </BaseTypography>
        <TemplateLandingButtonView
          v-if="buttons.centerHeader"
          full-width
          :button="buttons.centerHeader"
        />
      </div>
      <div class="three-template__header-item">
        <BaseTypography with-pre-line type="h3" weight="600">
          {{ data.rightSectionTitle }}
        </BaseTypography>
        <BaseTypography with-pre-line type="text" weight="400">
          {{ data.rightSectionDescription }}
        </BaseTypography>
        <TemplateLandingButtonView
          v-if="buttons.rightHeader"
          full-width
          :button="buttons.rightHeader"
        />
      </div>
    </div>

    <!-- Секция с текстом слева и изображением справа -->
    <div class="three-template__text-image-section">
      <div class="three-template__text-content">
        <BaseTypography with-pre-line type="h3" weight="600">
          {{ data.textImageSectionTitle }}
        </BaseTypography>
        <BaseTypography with-pre-line type="text" weight="400">
          {{ data.textImageSectionDescription }}
        </BaseTypography>
      </div>
      <div class="three-template__text-image">
        <TemplateMediaView
          v-if="data.images.textImage"
          :src="data.images.textImage"
          :aspect-width="642"
          :aspect-height="320"
        />
      </div>
    </div>

    <!-- Дополнительный блок с заголовком и описанием -->
    <div class="three-template__additional-section">
      <BaseTypography with-pre-line type="h3" weight="600">
        {{ data.bottomSectionTitle }}
      </BaseTypography>
      <BaseTypography with-pre-line type="text" weight="400">
        {{ data.bottomSectionDescription }}
      </BaseTypography>
      <TemplateLandingButtonView
        v-if="buttons.additional"
        :button="buttons.additional"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { IThreeTemplateData, TemplateData } from '~/entities/projects/model/templates-interface'
import { computed } from 'vue'
import { useTemplateViewButtons } from '~/entities/projects/model/useTemplateViewButtons'
import TemplateLandingButtonView from '~/entities/projects/ui/templates/TemplateLandingButtonView.vue'
import TemplateMediaView from '~/entities/projects/ui/templates/TemplateMediaView.vue'
import { BaseTypography } from '~/shared/ui'

interface Props {
  templateData?: TemplateData | null
}

const props = withDefaults(defineProps<Props>(), {
  templateData: null,
})

const data = computed(() => {
  if (props.templateData) {
    return props.templateData as IThreeTemplateData
  }

  return {
    title: 'Как заполнить этот шаблон',
    description: 'Заголовок — напишите короткую фразу из 3–7 слов. Она должна быть простой и понятной. Заголовок должен передавать позитивное настроение. Например: про новый день, вдохновение, движение вперёд, радость, развитие. Не используйте сложные слова и длинные предложения.',
    leftSectionTitle: 'Подзаголовок',
    leftSectionDescription: 'Напишите текст объёмом до 1000 символов. Лучше разделить его на 2–3 абзаца, чтобы текст было удобно читать. Один абзац — это 2–4 предложения.\n\nПишите простым языком. Представьте, что вы объясняете мысль другу.',
    centerSectionTitle: 'Подзаголовок',
    centerSectionDescription: 'Текст должен быть лёгким, понятным и нейтральным. Он не должен быть привязан к конкретному продукту, компании или услуге. Это универсальный текст, который используется как пример для шаблона. Можно писать о вдохновении, новых возможностях, развитии.',
    rightSectionTitle: 'Подзаголовок',
    rightSectionDescription: 'Cпокойствии, ценности момента, движении вперёд или простых радостях жизни. Старайтесь: не использовать сложные профессиональные термины, не добавлять агрессивную рекламу, не писать слишком длинные предложения, не перегружать текст.',
    textImageSectionTitle: 'Подзаголовок',
    textImageSectionDescription: 'Важно, чтобы текст выглядел аккуратно в блоке и легко воспринимался при быстром чтении. Старайтесь не перегружать предложения и не делать их слишком длинными. Лучше разбивать текст на небольшие абзацы по несколько предложений, чтобы его было удобно читать даже при быстром просмотре страницы. Избегайте негативных формулировок, громких обещаний и излишнего пафоса. Текст должен выглядеть естественно и вызывать ощущение спокойствия и лёгкости. Если после прочтения создаётся ощущение ясности и простоты, значит текст подходит для шаблона. Главное — чтобы он был понятен любому человеку независимо от возраста и опыта.',
    bottomSectionTitle: 'Подзаголовок',
    bottomSectionDescription: 'Основной текст должен быть нейтральным, понятным и универсальным. Его задача — показать пример оформления, а не продать продукт или рассказать о конкретной теме. Пишите простым языком, без сложных терминов и специальных слов. Представьте, что этот текст будет читать человек любого возраста и опыта — он должен сразу понимать смысл без дополнительных объяснений. Текст должен быть разделён на несколько аккуратных абзацев, чтобы его было удобно воспринимать визуально. Избегайте слишком длинных предложений и сложных конструкций. Лучше выражать мысли коротко и ясно. Старайтесь, чтобы текст создавал лёгкое, позитивное настроение. Он может говорить о вдохновении, развитии, новых возможностях или ценности простых моментов. Главное — сохранить естественность и простоту. Если текст читается легко и не вызывает вопросов, значит он подходит для использования в шаблоне.',
    images: {
      leftMain: '/projects/project-template-4-bg-1.webp',
      rightMain: '/projects/project-template-4-bg-2.webp',
      textImage: '/projects/project-template-4-bg-3.webp',
    },
  }
})

const buttons = useTemplateViewButtons(() => props.templateData)
</script>

<style lang="scss" scoped>
.three-template {
  --main-image-max-width: 100%;
  --side-image-max-width: 100%;

  display: grid;
  gap: rem(60px);
  grid-template-columns: 1fr;

  @include bp-media('xs', 'sm', 'md') {
    gap: rem(40px);
  }

  > * {
    min-width: 0;
  }

  :deep(.base-typography--type-h3) {
    @include bp-media('md', 'lg') {
      font-size: rem(24px);
    }

  }

  &__header {
    display: grid;
    justify-items: center;
    gap: rem(16px);

    > * {
      min-width: 0;
      max-width: 100%;
    }
  }

  &__main-section {
    display: grid;
    width: 100%;
    grid-template-columns: 2.3fr 1fr;
    gap: rem(32px);

    @include bp-media('xs', 'sm') {
      grid-template-columns: 1fr;
      gap: rem(24px);
    }

    @include bp-media('md', 'lg') {
      gap: rem(16px);
    }
  }

  &__main-image {
    overflow: hidden;
    border-radius: rem(16px);
    display: grid;
  }

  &__side-image {
    overflow: hidden;
    border-radius: rem(16px);
    display: grid;
  }

  &__triple-headers {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: rem(32px);

    > * {
      min-width: 0;
    }

    @include bp-media('xs', 'sm') {
      grid-template-columns: 1fr;
      gap: rem(24px);
    }
  }

  &__header-item {
    display: grid;
    gap: rem(16px);

    > * {
      min-width: 0;
    }
  }

  &__text-image-section {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: rem(32px);

    @include bp-media('xs', 'sm', 'md', 'lg') {
      grid-template-columns: 1fr;
      gap: rem(24px);
    }
  }

  &__text-content {
    display: flex;
    flex-direction: column;
    height: fit-content;
    gap: rem(8px);
  }

  &__text-image {
    display: grid;
    border-radius: rem(16px);
    overflow: hidden;
  }

  &__additional-section {
    display: grid;
    gap: rem(16px);

    > * {
      min-width: 0;
    }
  }

}
</style>
